import React from 'react';
import { PreAuthNavbar } from '@/components/navigation/PreAuthNavbar';
import { PostAuthNavbar } from '@/components/navigation/PostAuthNavbar';
import { Footer } from '@/components/marketing/Footer';
import { useAuth } from '@/providers/AuthProvider';

const PrivacyPage: React.FC = () => {
    const { isAuthenticated } = useAuth();

    return (
        <div className="min-h-screen flex flex-col">
            {isAuthenticated ? <PostAuthNavbar /> : <PreAuthNavbar />}
            <main className="flex-1 max-w-4xl mx-auto px-4 py-16">
                <h1 className="text-4xl font-bold mb-8 text-center">Privacy Policy</h1>
                <p className="text-lg mb-8 text-muted-foreground text-center">
                    Your privacy is important to us. This policy explains how Arrowise collects, uses, and protects your information.
                </p>

                <div className="space-y-8">
                    <section>
                        <h2 className="text-2xl font-semibold mb-4">1. Information We Collect</h2>
                        <p className="mb-4">
                            We collect information you provide directly, such as your name, email address, and account details when you register or use our services.
                        </p>
                        <p className="mb-4">
                            During candidate assessment sessions, we collect:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Code edits, file uploads, and session recordings</li>
                            <li>AI chat prompts, responses, and token usage data</li>
                            <li>Session metadata including timestamps and step progress</li>
                            <li>Bring Your Own API Key (BYOK) data, which is hashed and encrypted for security</li>
                        </ul>
                        <p>
                            We also collect usage analytics, device information, and cookies to improve our service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">2. How We Use Information</h2>
                        <p className="mb-4">
                            We use your information to:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Provide and operate the Arrowise platform, including processing AI interactions</li>
                            <li>Facilitate technical interviews and assessment sessions</li>
                            <li>Improve our services and develop new features</li>
                            <li>Communicate with you about your account and our services</li>
                            <li>Process payments and manage subscriptions through Stripe</li>
                            <li>Ensure security and prevent fraud</li>
                            <li>Enable AI-assisted candidate assessments (Coming soon)</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">3. Sharing of Information</h2>
                        <p className="mb-4">
                            We do not sell your personal information to third parties. We may share information in the following circumstances:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>With AI providers (such as OpenAI) solely to process your requests when using our AI features</li>
                            <li>With payment processors (Stripe) to handle billing and subscriptions</li>
                            <li>As required by law or to protect our rights</li>
                            <li>With your consent or at your direction</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">4. Data Security</h2>
                        <p className="mb-4">
                            We implement industry-standard security measures to protect your data, including encryption of sensitive information like API keys and secure storage of session data. However, no method of transmission over the internet is 100% secure.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">5. Data Retention</h2>
                        <p className="mb-4">
                            We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Session data is typically retained for the duration of your subscription plus a reasonable period for review and compliance. You can request deletion of your data at any time.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">6. Your Choices</h2>
                        <p className="mb-4">
                            You have the right to:
                        </p>
                        <ul className="list-disc list-inside mb-4 space-y-2">
                            <li>Access and review your personal information</li>
                            <li>Update or correct your account information</li>
                            <li>Request deletion of your data</li>
                            <li>Opt out of non-essential communications</li>
                            <li>Manage your cookie preferences</li>
                        </ul>
                        <p>
                            To exercise these rights, contact us using the information below.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking</h2>
                        <p className="mb-4">
                            We use cookies and similar technologies to enhance your experience, remember your preferences, and analyze usage. You can control cookie settings through your browser.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">8. International Data Transfers</h2>
                        <p className="mb-4">
                            Your data may be processed and stored in different countries. We ensure appropriate safeguards are in place for international transfers.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">9. Changes to Policy</h2>
                        <p className="mb-4">
                            We may update this policy periodically. We will notify you of significant changes via email or through our platform. Continued use of Arrowise constitutes acceptance of the updated policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mb-4">10. Contact</h2>
                        <p className="mb-4">
                            If you have questions about this policy or wish to exercise your rights, contact us at:
                        </p>
                        <div className="space-y-2">
                            <p><strong>General inquiries:</strong> <a href="mailto:omar.benhmuda@outlook.com" className="text-primary underline">omar.benhmuda@outlook.com</a></p>
                            <p><strong>Support:</strong> <a href="mailto:omar.benhmuda@outlook.com" className="text-primary underline">omar.benhmuda@outlook.com</a></p>
                        </div>
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default PrivacyPage;
